'use client'

import { useAuthorization } from '@entities/authentication'
import type { KrPaymentOrder, PrepareKrPayment } from '@entities/kr-payment/model'
import { generateMerchantUid, usePaymentErrorStaticModel } from '@entities/payment'
import { userEntityQueries } from '@entities/users'
import { getCustomerUid, HandlePaymentQueue, PaymentActionParams } from '@features/payment'
import { preparedCustomData, preparedPaymentUrl } from '@features/payment/lib/prepared-payment'
import { rpc } from '@server/infrastructure/rpc'
import { env, productionAppEnvSchema } from '@shared/config/env'
import { getMetaContextHeaders } from '@shared/marketing/meta-context'
import { useSuspenseQuery } from '@tanstack/react-query'

type UsePaymentByCardParkingProps = {
  handlePaymentQueue: HandlePaymentQueue
  captureTrialContext?: boolean
  preparePayment?: PrepareKrPayment
  failSlackWebHook: (errorCode: Undefinable<string>, errorMessage: Undefinable<string>) => Promise<void>
}

export const usePaymentByCardParking = ({
  handlePaymentQueue,
  failSlackWebHook,
  preparePayment,
  captureTrialContext,
}: UsePaymentByCardParkingProps) => {
  const authorization = useAuthorization()
  const { ERROR_MESSAGES } = usePaymentErrorStaticModel()
  const { data: userInfo } = useSuspenseQuery({
    ...userEntityQueries.getCurrentUser({ bearerToken: authorization.bearerToken }),
  })

  return async (
    { customerUid, merchantName, amount, currency = 'KRW', customData, noticeUrl }: PaymentActionParams,
    issuedOrder?: KrPaymentOrder | null,
  ) => {
    const prepared = issuedOrder ?? (await preparePayment?.())
    if (preparePayment && !prepared) {
      return
    }

    const legacyCustomData = {
      ...preparedCustomData(customData, prepared),
      subscribe_id: customData.subscribe_id ?? '',
    }

    const response = await rpc.api.v1.payment['parking-payment-methods'].$post(
      {
        json: {
          customer_uid: getCustomerUid(customerUid),
          merchant_uid: prepared?.orderId ?? generateMerchantUid(userInfo.id),
          merchant_name: merchantName,
          amount: prepared?.paymentAmount ?? amount,
          currency: prepared?.currency ?? currency,
          payment_method: prepared?.paymentMethod,
          buyer_name: productionAppEnvSchema.safeParse(env.NEXT_PUBLIC_APP_ENV).success
            ? userInfo.name
            : `${userInfo.name}-${env.NEXT_PUBLIC_APP_ENV}`,
          buyer_tel: userInfo.phone,
          custom_data: legacyCustomData,
          notice_url: preparedPaymentUrl(new URL(noticeUrl), prepared).toString(),
        },
      },
      {
        headers: {
          Authorization: `Bearer ${authorization.bearerToken}`,
          ...(!prepared && captureTrialContext ? getMetaContextHeaders(userInfo.countryCode) : {}),
        },
      },
    )

    if (!response.ok) {
      await preparePayment?.cancel?.(prepared?.orderId)
      throw new Error(ERROR_MESSAGES.PAYMENT_FAILED)
    }

    const paymentState = await response.json()

    if (paymentState.status === 'pending') {
      await handlePaymentQueue({ status: 'pending', merchantUid: paymentState.merchantUid })
      return
    }

    if (paymentState.status === 'error') {
      await preparePayment?.cancel?.(prepared?.orderId)
      await failSlackWebHook(paymentState.code, paymentState.message)
      const errorMessage = paymentState.message || ERROR_MESSAGES.PAYMENT_FAILED
      throw new Error(errorMessage)
    }

    if ('prepared' in paymentState) {
      if (!paymentState.prepared || paymentState.orderStatus !== 'PAID') {
        throw new Error(ERROR_MESSAGES.PAYMENT_FAILED)
      }
      await handlePaymentQueue({ status: 'pending', merchantUid: paymentState.data.merchantUid })
      return
    }

    await handlePaymentQueue({
      status: paymentState.status,
      data: {
        impUid: paymentState.data.impUid,
        amount: paymentState.data.amount,
        merchantUid: paymentState.data.merchantUid,
        customerUid: paymentState.data.customerUid,
      },
    })
  }
}
